import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Select,
    MenuItem,
    Alert
} from '@mui/material';
import { useGetDatosParametricosQuery } from '../../../services/presentacionesApi';

interface ProfesionalModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    editData?: any;
    errorCargo?: string | null;
    idAvisoObra?: number | null;
    existingCuits?: string[];
}

const ProfesionalModal: React.FC<ProfesionalModalProps> = ({
    open,
    onClose,
    onSubmit,
    editData,
    errorCargo,
    idAvisoObra,
    existingCuits = []
}) => {
    const [nombre, setNombre] = useState<string>('');
    const [apellido, setApellido] = useState<string>('');
    const [cuitCuil, setCuitCuil] = useState<string>('');
    const [cargo, setCargo] = useState<string>('');
    const [representacion, setRepresentacion] = useState<string>('');
    const [propioContratado, setPropioContratado] = useState<string>('');
    const [tituloHabilitante, setTituloHabilitante] = useState<string>('');
    const [matriculaNumero, setMatriculaNumero] = useState<string>('');
    const [entidadOtorgante, setEntidadOtorgante] = useState<string>('');
    const [responsableFormulario, setResponsableFormulario] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>({});

    const { data: parametricData } = useGetDatosParametricosQuery();

    const isCargoResponsable = cargo === '1';
    const disableCamposExtra = isCargoResponsable && !idAvisoObra;
    const requiredCamposExtra = !isCargoResponsable;

    const cleanObject = (obj: Record<string, any>) => {
        return Object.fromEntries(
            Object.entries(obj).filter(
                ([, value]) => value !== null && value !== undefined && value !== ''
            )
        );
    };

    useEffect(() => {
        if (editData) {
            setNombre(editData.nombre || '');
            setApellido(editData.apellido || '');
            setCuitCuil(editData.cuit || '');
            setCargo(editData.idCargo ? String(editData.idCargo) : '');
            setRepresentacion(
                editData.idRepresentacion != null ? String(editData.idRepresentacion) : ''
            );
            setPropioContratado(
                editData.idTipoContratacion != null ? String(editData.idTipoContratacion) : ''
            );
            setTituloHabilitante(editData.titulo || '');
            setMatriculaNumero(editData.matricula || '');
            setEntidadOtorgante(editData.entidadOtorgante || '');
            setResponsableFormulario(!!editData.responsableFormulario);
        } else {
            setNombre('');
            setApellido('');
            setCuitCuil('');
            setCargo('');
            setRepresentacion('');
            setPropioContratado('');
            setTituloHabilitante('');
            setMatriculaNumero('');
            setEntidadOtorgante('');
            setResponsableFormulario(false);
        }
    }, [editData]);

    useEffect(() => {
        if (cargo === '1') {
            setResponsableFormulario(true);
            if (!editData) {
                setEntidadOtorgante('');
                setMatriculaNumero('');
                setTituloHabilitante('');
                setPropioContratado('');
                setRepresentacion('');
            }
        }
    }, [cargo, editData]);

    const handleCuitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (/^\d*$/.test(value) && value.length <= 11) {
            setCuitCuil(value);
        }
    };

    const handleSubmit = () => {
        const newErrors: { [key: string]: boolean } = {};
        if (!nombre.trim()) newErrors.nombre = true;
        if (!apellido.trim()) newErrors.apellido = true;
        if (!cuitCuil.trim()) newErrors.cuitCuil = true;
        if (cargo !== '1') {
            if (!representacion) newErrors.representacion = true;
            if (!propioContratado) newErrors.propioContratado = true;
            if (!tituloHabilitante.trim()) newErrors.tituloHabilitante = true;
            if (!matriculaNumero.trim()) newErrors.matriculaNumero = true;
            if (!entidadOtorgante.trim()) newErrors.entidadOtorgante = true;
        }
        setFieldErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            setError('Debe completar todos los campos.');
            return;
        }

        const cuilTrimmed = cuitCuil.trim();
        const cuilExistente = existingCuits.some(
            (c) => c === cuilTrimmed && cuilTrimmed !== (editData?.cuit ?? '')
        );
        if (cuilExistente) {
            setError(
                'Ya existe un profesional cargado con el mismo CUIL / CUIT / CUIP. No se pueden registrar distintos cargos con el mismo identificador.'
            );
            return;
        }

        // Validar campos obligatorios
        if (cargo === '1') {
            if (nombre.trim() && apellido.trim() && cuitCuil.trim()) {
                if (cuitCuil.length === 11) {
                    const selectedCargo = parametricData?.data.cargos?.find(
                        (c) => c.id.toString() === cargo
                    );

                    const data = {
                        nombre,
                        apellido,
                        cuit: cuitCuil,
                        idCargo: Number(cargo),
                        cargoNombre: selectedCargo?.nombre || '',
                        idRepresentacion: representacion || null,
                        representacionNombre:
                            parametricData?.data.representaciones?.find(
                                (r) => r.id.toString() === representacion
                            )?.nombre || '',
                        idTipoContratacion: propioContratado ? Number(propioContratado) : null,
                        tipoContratacionNombre:
                            parametricData?.data.tiposContratacion?.find(
                                (t) => t.id.toString() === propioContratado
                            )?.nombre || '',
                        titulo: tituloHabilitante,
                        matricula: matriculaNumero,
                        entidadOtorgante,
                        responsableFormulario
                    };

                    onSubmit(cleanObject(data));

                    // Limpiar campos y errores
                    setNombre('');
                    setApellido('');
                    setCuitCuil('');
                    setCargo('');
                    setRepresentacion('');
                    setPropioContratado('');
                    setTituloHabilitante('');
                    setMatriculaNumero('');
                    setEntidadOtorgante('');
                    setResponsableFormulario(false);
                    setError(null);
                } else {
                    setError('El CUIT/CUIL debe tener 11 dígitos.');
                }
            } else {
                setError('Debe completar los campos obligatorios.');
            }
        } else {
            if (
                nombre.trim() &&
                apellido.trim() &&
                cuitCuil.trim() &&
                cargo &&
                representacion &&
                propioContratado &&
                tituloHabilitante.trim() &&
                matriculaNumero.trim() &&
                entidadOtorgante.trim()
            ) {
                if (cuitCuil.length === 11) {
                    const selectedCargo = parametricData?.data.cargos?.find(
                        (c) => c.id.toString() === cargo
                    );
                    onSubmit({
                        nombre,
                        apellido,
                        cuit: cuitCuil,
                        idCargo: Number(cargo),
                        cargoNombre: selectedCargo?.nombre || '',
                        idRepresentacion: Number(representacion),
                        representacionNombre:
                            parametricData?.data.representaciones?.find(
                                (r) => r.id.toString() === representacion
                            )?.nombre || '',
                        idTipoContratacion: Number(propioContratado),
                        tipoContratacionNombre:
                            parametricData?.data.tiposContratacion?.find(
                                (t) => t.id.toString() === propioContratado
                            )?.nombre || '',
                        titulo: tituloHabilitante,
                        matricula: matriculaNumero,
                        entidadOtorgante,
                        responsableFormulario
                    });

                    // Limpiar campos y errores
                    setNombre('');
                    setApellido('');
                    setCuitCuil('');
                    setCargo('');
                    setRepresentacion('');
                    setPropioContratado('');
                    setTituloHabilitante('');
                    setMatriculaNumero('');
                    setEntidadOtorgante('');
                    setResponsableFormulario(false);
                    setError(null);
                } else {
                    setError('El CUIT/CUIL debe tener 11 dígitos.');
                }
            } else {
                setError('Debe completar todos los campos.');
            }
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{ ...modalStyle }}>
                <Box sx={{ maxHeight: '100vh', overflowY: 'auto', padding: '16px' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        Agregar Datos Laborales del Profesional
                    </Typography>
                    <TextField
                        label="Nombre"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        fullWidth
                        sx={{ mt: 2 }}
                        required
                        error={fieldErrors.nombre}
                        helperText={fieldErrors.nombre}
                    />
                    <TextField
                        label="Apellido"
                        value={apellido}
                        onChange={(e) => setApellido(e.target.value)}
                        fullWidth
                        sx={{ mt: 2 }}
                        required
                        error={fieldErrors.apellido}
                        helperText={fieldErrors.apellido}
                    />
                    <TextField
                        label="CUIT/CUIL"
                        value={cuitCuil}
                        onChange={handleCuitChange}
                        fullWidth
                        sx={{ mt: 2 }}
                        inputProps={{ maxLength: 11 }}
                        required
                        error={fieldErrors.cuitCuil}
                        helperText={
                            fieldErrors.cuitCuil
                                ? 'Campo obligatorio'
                                : cuitCuil && cuitCuil.length !== 11
                                ? 'Debe tener 11 dígitos'
                                : ''
                        }
                    />

                    <Select
                        value={cargo}
                        onChange={(e) => setCargo(e.target.value as string)}
                        fullWidth
                        sx={{ mt: 2 }}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Seleccionar cargo' }}
                        error={fieldErrors.cargo}
                    >
                        <MenuItem disabled value="">
                            Cargo
                        </MenuItem>
                        {parametricData?.data.cargos
                            ?.filter((c) => {
                                if (idAvisoObra) {
                                    return c.id === 1 || c.id === 2;
                                }
                                return true;
                            })
                            .map((cargo) => (
                                <MenuItem key={cargo.id} value={cargo.id.toString()}>
                                    {cargo.nombre}
                                </MenuItem>
                            ))}
                    </Select>
                    {fieldErrors.cargo && (
                        <Typography color="error" variant="caption">
                            Campo obligatorio
                        </Typography>
                    )}

                    <Select
                        value={representacion}
                        onChange={(e) => setRepresentacion(e.target.value as string)}
                        fullWidth
                        sx={{ mt: 2 }}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Seleccionar representación' }}
                        required={requiredCamposExtra}
                        disabled={disableCamposExtra}
                        error={fieldErrors.representacion}
                    >
                        <MenuItem disabled value="">
                            Representación
                        </MenuItem>
                        {parametricData?.data.representaciones?.map((representacion) => (
                            <MenuItem key={representacion.id} value={representacion.id.toString()}>
                                {representacion.nombre}
                            </MenuItem>
                        ))}
                    </Select>
                    {fieldErrors.representacion && (
                        <Typography color="error" variant="caption">
                            Campo obligatorio
                        </Typography>
                    )}

                    <Select
                        value={propioContratado}
                        onChange={(e) => setPropioContratado(e.target.value as string)}
                        fullWidth
                        sx={{ mt: 2 }}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Seleccionar Tipo de Contrato' }}
                        required={requiredCamposExtra}
                        disabled={disableCamposExtra}
                        error={fieldErrors.propioContratado}
                    >
                        <MenuItem disabled value="">
                            Tipo de Contrato
                        </MenuItem>
                        {parametricData?.data.tiposContratacion?.map((tipo) => (
                            <MenuItem key={tipo.id} value={tipo.id.toString()}>
                                {tipo.nombre}
                            </MenuItem>
                        ))}
                    </Select>
                    {fieldErrors.propioContratado && (
                        <Typography color="error" variant="caption">
                            Campo obligatorio
                        </Typography>
                    )}

                    <TextField
                        label="Título Habilitante"
                        value={tituloHabilitante}
                        onChange={(e) => setTituloHabilitante(e.target.value)}
                        fullWidth
                        sx={{ mt: 2 }}
                        required={requiredCamposExtra}
                        disabled={disableCamposExtra}
                        error={fieldErrors.tituloHabilitante}
                        helperText={fieldErrors.tituloHabilitante ? 'Campo obligatorio' : ''}
                    />
                    <TextField
                        label="Matrícula Número"
                        value={matriculaNumero}
                        onChange={(e) => setMatriculaNumero(e.target.value)}
                        fullWidth
                        sx={{ mt: 2 }}
                        required={requiredCamposExtra}
                        disabled={disableCamposExtra}
                        error={fieldErrors.matriculaNumero}
                        helperText={fieldErrors.matriculaNumero ? 'Campo obligatorio' : ''}
                    />
                    <TextField
                        label="Entidad Otorgante"
                        value={entidadOtorgante}
                        onChange={(e) => setEntidadOtorgante(e.target.value)}
                        fullWidth
                        sx={{ mt: 2 }}
                        required={requiredCamposExtra}
                        disabled={disableCamposExtra}
                        error={fieldErrors.entidadOtorgante}
                        helperText={fieldErrors.entidadOtorgante ? 'Campo obligatorio' : ''}
                    />

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {errorCargo && <Alert severity="error">{errorCargo}</Alert>}

                    <Box sx={{ mt: 3, textAlign: 'right' }}>
                        <Button onClick={onClose} sx={{ mr: 2 }}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSubmit} variant="contained" color="primary">
                            Guardar
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

const modalStyle = {
    position: 'absolute' as 'absolute',
    height: '100%',
    width: '40%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 1,
    p: 1,
    right: '0px'
};

export default ProfesionalModal;
